import { useSnackbar } from "@sagecentral/core-ui";
import { ValidationSummary } from "components/shared";
import { useCentralApi, useCurrentUser } from "hooks/shared";
import { useCallback, useState } from "react";

export interface IAddRelatedWorkItemEntry {
    id: number;
    child: boolean;
};

export type AddRelatedWorkItemsPayload = IAddRelatedWorkItemEntry[];
type AddRelatedWorkItemsOpenSignature = (payload: AddRelatedWorkItemsPayload, workspaceId?: number) => Promise<boolean>;

type UseAddRelatedWorkItemsResult = [
    AddRelatedWorkItemsOpenSignature,
    boolean
];
type UseAddRelatedWorkItemsSignature = (workItemId: number, draft?: boolean) => UseAddRelatedWorkItemsResult;

const useAddRelatedWorkItems: UseAddRelatedWorkItemsSignature = (workItemId, draft) => {
    const [ working, setWorking ] = useState<boolean>(false);
    const user = useCurrentUser();
    const fetcher = useCentralApi(user);
    const { displaySnackbar } = useSnackbar();

    const addRelated = useCallback(async (payload: AddRelatedWorkItemsPayload, workspaceId?: number) => {
        if (!user || !user.access_token) return false;
        if (workItemId === 0) return false;
        
        setWorking(true);
        try {
            let url = draft 
                ? `/api/v3/drafts/workitems/${workItemId}/related`
                : `/api/v3/workitems/${workItemId}/related`;
            if (workspaceId) {
                url += `?workspaceId=${workspaceId}`;
            }
            
            const resp = await fetcher(url, {
                method: 'POST',
                body: JSON.stringify(payload)
            }, async r => {
                if (r.status === 400) {
                    const data = await r.json();
                    displaySnackbar('warning', <ValidationSummary validationDetail={data} />);
                    return true;
                } else {
                    displaySnackbar('error', "We couldn't relate the work item(s).");
                }
                return true;
            });

            if (!resp.ok) {
                return false;
            }

            displaySnackbar('success', 'Related work item(s) added.');
            return true;
        } catch {
            return false;
        } finally {
            setWorking(false);
        }
    }, [ user, workItemId, draft, displaySnackbar ]);

    return [
        addRelated,
        working
    ];
};

export default useAddRelatedWorkItems;